'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { JsonViewer } from '@/components/ui/json-viewer';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

import { useReplayWebhookDelivery, useWebhookDeliveries } from '@/lib/api/integrations';
import { parseDeliveryBody, truncationNote } from '@/lib/webhook-delivery';

interface WebhookDeliveriesDialogProps {
  endpoint: { id: string; url: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Mirrors the caps in src/app/useCases/webhooks/getWebhookDeliveries.ts — only used for the
// "showing the first N characters" hint, the API is the source of truth for the cut itself.
const PAYLOAD_MAX_CHARS = 10_000;
const RESPONSE_MAX_CHARS = 4_000;

type Delivery = NonNullable<ReturnType<typeof useWebhookDeliveries>['data']>[number];

const DeliveryStatusBadge = ({
  status,
  responseStatus,
}: {
  status: string;
  responseStatus: number | null;
}) => {
  if (responseStatus === null) {
    // No HTTP status means the request never got a response — timeout, DNS, refused.
    return status === 'failed' ? (
      <Badge variant="red" className="text-xs">
        No response
      </Badge>
    ) : (
      <span className="text-muted-foreground text-xs">—</span>
    );
  }

  const isSuccess = responseStatus >= 200 && responseStatus < 300;
  const isClientError = responseStatus >= 400 && responseStatus < 500;
  const variant = isSuccess ? 'green' : isClientError ? 'yellow' : 'red';

  return (
    <Badge variant={variant} className="font-mono text-xs">
      {responseStatus}
    </Badge>
  );
};

/** One body (request payload or response) — pretty JSON when it parses, raw text otherwise. */
const DeliveryBody = ({
  title,
  raw,
  isTruncated,
  maxChars,
  emptyLabel,
  variant = 'default',
}: {
  title: string;
  raw: string | null;
  isTruncated: boolean;
  maxChars: number;
  emptyLabel: string;
  variant?: 'default' | 'error';
}) => {
  const parsed = parseDeliveryBody(raw);
  const note = truncationNote(isTruncated, maxChars);

  if (parsed.kind === 'empty') {
    return (
      <div className="bg-muted/50 rounded-md border p-3">
        <div className="mb-2 text-xs font-medium">{title}</div>
        <p className="text-muted-foreground text-xs">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-1">
      {parsed.kind === 'json' ? (
        <JsonViewer
          data={parsed.value}
          title={title}
          variant={variant}
          className="max-h-72 overflow-y-auto"
        />
      ) : (
        <div
          className={cn(
            'max-h-72 overflow-y-auto rounded-md border p-3',
            variant === 'error'
              ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/10'
              : 'bg-muted',
          )}
        >
          <div className="mb-2 text-xs font-medium">{title}</div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs">{parsed.value}</pre>
        </div>
      )}
      {note && <p className="text-muted-foreground text-[11px]">{note}</p>}
    </div>
  );
};

/** The expanded panel under a delivery row: what we sent, what came back. */
const DeliveryDetails = ({
  delivery,
  onReplay,
  isReplaying,
}: {
  delivery: Delivery;
  onReplay: (eventId: string) => void;
  isReplaying: boolean;
}) => {
  const responseTitle =
    delivery.responseStatus !== null ? `Response · HTTP ${delivery.responseStatus}` : 'Response';
  const hasError = !!delivery.errorMessage;

  return (
    <div className="space-y-3 py-1">
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
        <span>
          Attempt <span className="text-foreground font-medium">{delivery.attemptCount}</span>
        </span>
        <span className="min-w-0 truncate" title={delivery.requestUrl}>
          POST <span className="font-mono">{delivery.requestUrl}</span>
        </span>
        {delivery.eventId && (
          <span className="min-w-0 truncate" title={delivery.eventId}>
            Event <span className="font-mono">{delivery.eventId}</span>
          </span>
        )}
        <span>{format(new Date(delivery.createdAt), 'MMM dd, yyyy HH:mm:ss')}</span>
        {delivery.status === 'failed' && delivery.eventId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-[11px]"
            disabled={isReplaying}
            onClick={(event) => {
              event.stopPropagation();
              onReplay(delivery.eventId!);
            }}
          >
            <RotateCcw className="h-3 w-3" />
            Replay
          </Button>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <DeliveryBody
          title="Request payload"
          raw={delivery.requestPayload}
          isTruncated={delivery.requestPayloadTruncated}
          maxChars={PAYLOAD_MAX_CHARS}
          emptyLabel="Payload not recorded for this delivery"
        />
        {hasError ? (
          <DeliveryBody
            title={responseTitle}
            raw={delivery.errorMessage}
            isTruncated={false}
            maxChars={RESPONSE_MAX_CHARS}
            emptyLabel="No response body"
            variant="error"
          />
        ) : (
          <DeliveryBody
            title={responseTitle}
            raw={delivery.responseBody}
            isTruncated={delivery.responseBodyTruncated}
            maxChars={RESPONSE_MAX_CHARS}
            emptyLabel="No response body"
          />
        )}
      </div>
    </div>
  );
};

export function WebhookDeliveriesDialog({ endpoint, open, onOpenChange }: WebhookDeliveriesDialogProps) {
  const { data: deliveries, isLoading, isError } = useWebhookDeliveries(endpoint?.id ?? '', open);
  const replayMutation = useReplayWebhookDelivery();
  const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<string>>(() => new Set());

  const handleReplay = async (eventId: string) => {
    if (!endpoint?.id) return;
    try {
      const result = await replayMutation.mutateAsync({
        webhookEndpointId: endpoint.id,
        eventId,
      });
      if (result.replayed) {
        toast.success('Webhook replay queued');
      } else {
        toast.message('Replay skipped', {
          description: 'This delivery is not eligible for replay from the queue.',
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to replay webhook');
    }
  };

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // A fresh open starts collapsed, whatever was expanded last time.
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setExpandedIds(new Set());
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle>Delivery history</DialogTitle>
          <DialogDescription className="truncate font-mono" title={endpoint?.url}>
            {endpoint?.url}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : isError ? (
          <div className="py-8 text-center">
            <p className="text-destructive text-sm">Failed to load deliveries</p>
          </div>
        ) : !deliveries || deliveries.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-muted-foreground text-sm">No deliveries yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>When</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((delivery) => {
                const preview = delivery.errorMessage || delivery.responseBody;
                const isExpanded = expandedIds.has(delivery.id);
                const panelId = `webhook-delivery-${delivery.id}`;
                return (
                  <React.Fragment key={delivery.id}>
                    <TableRow
                      className="cursor-pointer"
                      data-state={isExpanded ? 'open' : 'closed'}
                      onClick={() => toggle(delivery.id)}
                    >
                      <TableCell className="p-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          aria-expanded={isExpanded}
                          aria-controls={panelId}
                          aria-label={isExpanded ? 'Hide delivery details' : 'Show delivery details'}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle(delivery.id);
                          }}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {format(new Date(delivery.createdAt), 'MMM dd, HH:mm')}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{delivery.eventType}</TableCell>
                      <TableCell>
                        <DeliveryStatusBadge
                          status={delivery.status}
                          responseStatus={delivery.responseStatus}
                        />
                      </TableCell>
                      <TableCell className="max-w-0">
                        {preview ? (
                          <div className="text-muted-foreground truncate text-xs" title={preview}>
                            {preview}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow id={panelId} className="hover:bg-transparent">
                        <TableCell colSpan={5} className="bg-muted/30 px-4">
                          <DeliveryDetails
                            delivery={delivery}
                            onReplay={handleReplay}
                            isReplaying={replayMutation.isPending}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
